import { Visibility, VisibilityOff } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useGenericDevice from 'hooks/useGenericDevice';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import type { FC } from 'react';

import ActivityLabel from 'components/ActivityLabel';
import DeviceIconComponent from 'components/DeviceIconComponent';
import ErrorText from 'components/ErrorText';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import {
  groupConfigDevices,
  removePendingDeviceConfig,
} from 'redux-store/slices/groupConfigDevices';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';
import deleteDeviceConfigQueueThunk from 'redux-store/thunks/groupConfigDevices/deleteDeviceConfigQueueThunk';
import reapplyProtectedConfigurationThunk from 'redux-store/thunks/groupConfigDevices/reapplyProtectedConfigurationThunk';

import type { AddLoadBalancingGroupModalStageProps } from '..';

const ProtectedCharger: FC<AddLoadBalancingGroupModalStageProps> = ({
  setState,
  onClose,
  state,
  selectedGroup,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const profile = useProfile();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [technicianPassword, setTechnicianPassword] = useState('');
  const [technicianPasswordError, setTechnicianPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const friendlyName = useGenericDevice(state.serial, s => s?.fna);

  const selectedDevice = useAppSelector(s =>
    s.devices.items.find(device => device.serial === state.serial),
  );

  const handleDeletePendingDeviceConfigQueue = useCallback(async () => {
    if (!selectedDevice?.id) return;

    setLoading(true);

    try {
      const result = await dispatch(
        deleteDeviceConfigQueueThunk(selectedDevice.id),
      ).unwrap();

      if (result.success) {
        dispatch(removePendingDeviceConfig(selectedDevice.serial));

        setTimeout(() => {
          if (profile?.id) {
            dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          setLoading(false);
          setError('');
          if (selectedGroup && state.pendingDevices.length === 1) {
            onClose();
          } else {
            setState(prev => ({
              ...prev,
              pendingDevices: prev.pendingDevices.filter(
                device => device.serial !== selectedDevice.serial,
              ),
              stage: 'success',
            }));
          }
        }, 1250);
      } else {
        setLoading(false);
        setError(
          t('common:pages.device_setup.troubleshootingModal.unable_to_remove'),
        );
      }
    } catch (err) {
      setLoading(false);
      setError((err as Error).message || t('common:unknown_error'));

      Sentry.captureException(err, {
        extra: { deviceId: selectedDevice.id },
        tags: { page: 'ProtectedCharger' },
      });
    }
  }, [
    state.pendingDevices,
    selectedDevice?.id,
    selectedDevice?.serial,
    dispatch,
    profile?.id,
    selectedGroup,
    onClose,
    setState,
    t,
  ]);

  const handleVerifyTechnicianPassword = useCallback(async () => {
    if (!selectedDevice?.id) return;

    setError(null);
    setTechnicianPasswordError('');

    if (!technicianPassword || technicianPassword.length < 8) {
      setTechnicianPasswordError(
        t('common:pages.settings.subpages.password.error.password_too_short'),
      );

      return;
    }

    setLoading(true);

    try {
      const result = await dispatch(
        reapplyProtectedConfigurationThunk({
          technicianPassword: { technicianPassword },
          deviceId: selectedDevice.id,
        }),
      ).unwrap();

      if (result.success) {
        dispatch(removePendingDeviceConfig(selectedDevice.serial));

        dispatch(
          groupConfigDevices([
            {
              serial: selectedDevice.serial,
              success: result.success,
              configurations: [],
            },
          ]),
        );

        setTimeout(() => {
          if (profile?.id) {
            dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          setLoading(false);
          setTechnicianPassword('');
          if (selectedGroup && state.pendingDevices.length === 1) {
            onClose();
          } else {
            setState(prev => ({
              ...prev,
              pendingDevices: prev.pendingDevices.filter(
                device => device.serial !== selectedDevice.serial,
              ),
              stage: 'success',
            }));
          }
        }, 1250);
      } else {
        setLoading(false);
        if ('error' in result) {
          setError(result.error as string);
        }

        return;
      }
    } catch (err) {
      setLoading(false);
      setError((err as Error).message || t('common:unknown_error'));
    }
  }, [
    state.pendingDevices,
    selectedDevice?.id,
    selectedDevice?.serial,
    technicianPassword,
    t,
    dispatch,
    profile?.id,
    selectedGroup,
    onClose,
    setState,
  ]);

  return (
    <Box mb={3} height="100%" display="flex" flexDirection="column">
      <Typography variant="h2" textAlign="center" mb={2}>
        {t(
          'common:pages.device_setup.troubleshootingModal.restrictedConfigTitle',
        )}
      </Typography>
      <Typography variant="body1" textAlign="center">
        {t(
          'common:pages.device_setup.troubleshootingModal.troubleshootingDescProtected',
        )}
      </Typography>
      <ErrorText mt={1} error={error} />
      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        overflow="auto"
        flexGrow={0}
        flexShrink={1}
        flexBasis="auto"
        width="100%"
      >
        <List sx={{ borderRadius: 8 }}>
          <ListItem
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }}
            divider
          >
            <ListItemIcon>
              <DeviceIconComponent
                typ={selectedDevice?.type}
                subtype={selectedDevice?.subtype}
                outlined
              />
            </ListItemIcon>
            <ListItemText
              primary={
                friendlyName ??
                t('common:serial_number_big_with_value', {
                  value: selectedDevice?.serial,
                })
              }
              secondary={t('common:serial_number_with_value', {
                value: selectedDevice?.serial,
              })}
            />
            <ActivityLabel status="Protected" />
          </ListItem>
          <ListItemButton
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
            }}
            disabled={isLoading}
            onClick={handleDeletePendingDeviceConfigQueue}
          >
            <ListItemText
              primary={t(
                'common:pages.device_setup.troubleshootingModal.remove_queue',
              )}
              primaryTypographyProps={{
                style: {
                  color: isLoading
                    ? theme.vars.palette.grey[500]
                    : theme.vars.palette.error.main,
                  fontWeight: 600,
                },
              }}
            />
            <ListItemIcon
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <CancelOutlinedIcon color="error" />
            </ListItemIcon>
          </ListItemButton>
        </List>
        <Box mt={4}>
          <FilledTextField
            label={t(
              'common:pages.settings.subpages.password.technician_password',
            )}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={technicianPassword}
            onChange={e => setTechnicianPassword(e.target.value)}
            error={!!technicianPasswordError}
            helperText={technicianPasswordError}
            fullWidth
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 4 }}
            onClick={handleVerifyTechnicianPassword}
            disabled={
              !technicianPassword || technicianPassword.length < 8 || isLoading
            }
          >
            {t('common:continue')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProtectedCharger;
